import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Newspaper,
  Mail,
  Users,
  Send,
  RefreshCw,
  Plus,
  Trash2,
  XCircle,
  Eye,
  Edit,
  Check,
  X,
  Clock,
  AlertCircle,
  CheckCircle,
  FileText,
  Download,
  Upload,
  Search,
  Filter,
  Globe,
  Loader2,
  ChevronRight,
  ExternalLink,
  MailCheck,
  MailX,
  UserX,
  Settings,
  CalendarDays,
  ImageIcon,
  Sparkles,
  Archive,
  Copy,
  Ban,
  ShieldX,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { API_URL, API_BASE_URL } from "../config/api";
import { RoadClosureSection } from "../components/RoadClosureSection";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";


// Network badge colors
const NETWORK_COLORS = {
  WFN: "bg-blue-100 text-blue-800",
  TWIG: "bg-green-100 text-green-800",
  UOL: "bg-purple-100 text-purple-800",
  GAL: "bg-orange-100 text-orange-800",
  XLP: "bg-pink-100 text-pink-800",
  SBC: "bg-yellow-100 text-yellow-800",
  CRM: "bg-cyan-100 text-cyan-800",
  KOREA: "bg-red-100 text-red-800",
  EMAIL_ARCHIVE: "bg-amber-100 text-amber-800",
  OFFERS: "bg-emerald-100 text-emerald-800",
};

// Source icons/colors
const SOURCE_CONFIG = {
  gcaptain: { name: "gCaptain", color: "bg-blue-500" },
  splash247: { name: "Splash 247", color: "bg-cyan-500" },
  theloadstar: { name: "The Loadstar", color: "bg-indigo-500" },
  asianshipper: { name: "Asian Shipper", color: "bg-red-500" },
  inboundlogistics: { name: "Inbound Logistics", color: "bg-green-500" },
  adriaports: { name: "Adria Ports", color: "bg-purple-500" },
  finance_si: { name: "Finance.si", color: "bg-orange-500" },
};

// Default newsletter sections
const DEFAULT_SECTIONS = [
  { title: "Market Outlook & Industry Trends", emoji: "🚚", articles: [] },
  { title: "Regional News (Slovenia & Adriatic)", emoji: "🇸🇮", articles: [] },
  { title: "Shipping & Maritime", emoji: "🚢", articles: [] },
  { title: "Air & Global Transport", emoji: "✈️", articles: [] },
  { title: "Supply Chain & Technology", emoji: "🧠", articles: [] },
];

export default function NewsletterPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState("articles");
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  
  // Articles state
  const [articles, setArticles] = useState([]);
  const [articlesTotal, setArticlesTotal] = useState(0);
  const [articleFilter, setArticleFilter] = useState({ category: "", source: "", unused: false, hideCompetitor: false, localFirst: true });
  const [selectedArticles, setSelectedArticles] = useState([]);
  
  // Email lists state
  const [emailStats, setEmailStats] = useState({ networks: {}, total_active: 0, total_bounced: 0, total_unsubscribed: 0 });
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [networkEmails, setNetworkEmails] = useState([]);
  const [emailSearch, setEmailSearch] = useState("");
  
  // Drafts state
  const [drafts, setDrafts] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  
  // Draft editor state
  const [showDraftEditor, setShowDraftEditor] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [draftForm, setDraftForm] = useState({
    subject: `[EUROPACIFIC] Logistics & Supply Chain Newsletter — ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
    greeting: "Dear Valued Logistics Partner,",
    sections: JSON.parse(JSON.stringify(DEFAULT_SECTIONS)),
    cta_text: "Contact our sales team today for updated rate levels and sailing schedules",
    cta_email: "info@europacific.com",
    cta_website: "www.europacific.com",
    target_lists: [],
    scheduled_date: "",
  });
  
  // Import emails dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importNetwork, setImportNetwork] = useState("");
  const [importEmails, setImportEmails] = useState("");
  
  // Korea LCL state
  const [koreaLCLSchedule, setKoreaLCLSchedule] = useState([]);
  const [koreaLCLContacts, setKoreaLCLContacts] = useState(null);
  const [koreaLCLTargets, setKoreaLCLTargets] = useState({ total: 0, contacts: [] });
  const [koreaLCLPreview, setKoreaLCLPreview] = useState("");
  const [showKoreaLCLPreview, setShowKoreaLCLPreview] = useState(false);
  const [uploadingPDF, setUploadingPDF] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Free Newsletter state
  const [freeNewsletterForm, setFreeNewsletterForm] = useState({
    subject: "",
    content: "",
    images: [],           // Array of {url, type: 'inline' | 'attachment', placeholder: 'IMAGE_1'}
    target_sources: [],   // Which sources to pull emails from
    target_networks: [],  // Newsletter networks
    target_tags: [],      // CRM company tags
  });
  const [freeNewsletterPreview, setFreeNewsletterPreview] = useState("");
  const [showFreeNewsletterPreview, setShowFreeNewsletterPreview] = useState(false);
  const [allEmailSources, setAllEmailSources] = useState({
    newsletter_networks: [],
    crm_tags: [],
    total_recipients: 0,
  });
  const [uploadingFreeImage, setUploadingFreeImage] = useState(false);
  const [sendingFreeNewsletter, setSendingFreeNewsletter] = useState(false);
  
  // AI Newsletter Generation state
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiVersions, setAiVersions] = useState([]);  // Multiple AI-generated versions
  const [selectedAiVersion, setSelectedAiVersion] = useState(null);
  const [showAiVersionsModal, setShowAiVersionsModal] = useState(false);
  const [aiRefinementRound, setAiRefinementRound] = useState(1);  // Track refinement round
  const [expandedPreview, setExpandedPreview] = useState(null);  // For full preview modal
  
  // Offer Archive / Email Repository state
  const [offerRepoEmails, setOfferRepoEmails] = useState([]);
  const [offerRepoTotal, setOfferRepoTotal] = useState(0);
  const [offerRepoSearch, setOfferRepoSearch] = useState("");
  const [offerSyncStats, setOfferSyncStats] = useState(null);
  const [offerSyncing, setOfferSyncing] = useState(false);

  // Imported Email List state (from Import/Export page)
  const [importedEmails, setImportedEmails] = useState([]);
  const [importedEmailsTotal, setImportedEmailsTotal] = useState(0);
  const [importedEmailSearch, setImportedEmailSearch] = useState("");
  const [importedEmailTags, setImportedEmailTags] = useState([]);
  const [selectedImportedTag, setSelectedImportedTag] = useState("");

  // All Email Lists state (unified view)
  const [allEmails, setAllEmails] = useState([]);
  const [allEmailsTotal, setAllEmailsTotal] = useState(0);
  const [allEmailsStats, setAllEmailsStats] = useState({ total: 0, active: 0, bounced: 0, unsubscribed: 0 });
  const [allEmailsNetworks, setAllEmailsNetworks] = useState([]);
  const [allEmailsTags, setAllEmailsTags] = useState([]);
  const [allEmailsFilter, setAllEmailsFilter] = useState({ status: "active", network: "", tag: "", search: "" });
  const [syncing, setSyncing] = useState(false);

  // Newsletter Archive state
  const [sentNewsletters, setSentNewsletters] = useState([]);
  const [sentNewslettersTotal, setSentNewslettersTotal] = useState(0);
  const [sentNewslettersPage, setSentNewslettersPage] = useState(1);
  const [selectedArchiveNewsletter, setSelectedArchiveNewsletter] = useState(null);
  const [showArchiveDetailModal, setShowArchiveDetailModal] = useState(false);

  // Suppression List state
  const [suppressionList, setSuppressionList] = useState([]);
  const [suppressionStats, setSuppressionStats] = useState({});
  const [suppressionSearch, setSuppressionSearch] = useState("");
  const [suppressionFilter, setSuppressionFilter] = useState("");
  const [showAddSuppressionModal, setShowAddSuppressionModal] = useState(false);
  const [newSuppressionEmail, setNewSuppressionEmail] = useState("");
  const [newSuppressionReason, setNewSuppressionReason] = useState("manual");

  // Newsletter delivery stats
  const [newsletterStats, setNewsletterStats] = useState({
    email_list: { total: 0, active: 0, bounced: 0, unsubscribed: 0, cleaned: 0 },
    newsletters: { total_sent_count: 0, total_failed_count: 0, recent_newsletters: 0 },
    delivery: { newsletter_emails_sent: 0, newsletter_emails_failed: 0 }
  });

  // Batch send state
  const [showBatchSendDialog, setShowBatchSendDialog] = useState(false);
  const [batchSendConfig, setBatchSendConfig] = useState({
    draftId: "",
    batchSize: 50,
    delayBetweenEmails: 0.5,
    delayBetweenBatches: 10,
    targetTags: [],
    targetNetworks: [],
    excludeRecentDays: 7
  });
  const [sendingBatch, setSendingBatch] = useState(false);
  const [sendProgress, setSendProgress] = useState(null);

  // Auth headers
  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  // Fetch articles
  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (articleFilter.category) params.append("category", articleFilter.category);
      if (articleFilter.source) params.append("source", articleFilter.source);
      if (articleFilter.unused) params.append("unused_only", "true");
      params.append("limit", "50");
      
      const response = await axios.get(`${API_URL}/newsletter/articles?${params}`, getAuthHeaders());
      setArticles(response.data.articles || []);
      setArticlesTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Failed to fetch articles");
    } finally {
      setLoading(false);
    }
  }, [articleFilter, getAuthHeaders]);

  // Trigger article scraping
  const triggerScraping = async () => {
    try {
      setScraping(true);
      await axios.post(`${API_URL}/newsletter/scrape-articles`, {}, getAuthHeaders());
      toast.success("Article scraping started! This may take a few minutes.");
      
      // Poll for new articles after delay
      setTimeout(() => {
        fetchArticles();
        setScraping(false);
      }, 30000);
    } catch (error) {
      toast.error("Failed to start scraping");
      setScraping(false);
    }
  };

  // Fetch email stats
  const fetchEmailStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/newsletter/email-lists`, getAuthHeaders());
      setEmailStats(response.data);
    } catch (error) {
      console.error("Failed to fetch email stats:", error);
    }
  }, [getAuthHeaders]);

  // Fetch network emails
  const fetchNetworkEmails = useCallback(async (network) => {
    if (!network) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (emailSearch) params.append("search", emailSearch);
      params.append("limit", "100");
      
      const response = await axios.get(`${API_URL}/newsletter/email-lists/${network}?${params}`, getAuthHeaders());
      setNetworkEmails(response.data.emails || []);
    } catch (error) {
      toast.error("Failed to fetch emails");
    } finally {
      setLoading(false);
    }
  }, [emailSearch, getAuthHeaders]);

  // Fetch offer repository emails (from offer@europacific.com)
  const fetchOfferRepository = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        limit: "100",
      });
      if (offerRepoSearch) params.append("search", offerRepoSearch);
      
      const response = await axios.get(`${API_URL}/offer-archive/email-repository?${params}`, getAuthHeaders());
      setOfferRepoEmails(response.data.emails || []);
      setOfferRepoTotal(response.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch offer repository:", error);
    }
  }, [offerRepoSearch, getAuthHeaders]);

  // Fetch offer sync stats
  const fetchOfferSyncStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/offer-archive/stats`, getAuthHeaders());
      setOfferSyncStats(response.data);
    } catch (error) {
      console.error("Failed to fetch offer stats:", error);
    }
  }, [getAuthHeaders]);

  // Fetch newsletter stats overview
  const fetchNewsletterStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/newsletter/stats/overview`, getAuthHeaders());
      setNewsletterStats(response.data);
    } catch (error) {
      console.error("Failed to fetch newsletter stats:", error);
    }
  }, [getAuthHeaders]);

  // Fetch all available email sources for Free Newsletter
  const fetchAllEmailSources = useCallback(async () => {
    try {
      // Get newsletter networks
      const networksRes = await axios.get(`${API_URL}/newsletter/email-lists`, getAuthHeaders());
      const networks = Object.keys(networksRes.data.networks || {});
      
      // Get CRM company tags
      const tagsRes = await axios.get(`${API_URL}/crm/companies/tags`, getAuthHeaders());
      const tags = tagsRes.data.tags || [];
      
      setAllEmailSources({
        newsletter_networks: networks,
        crm_tags: tags,
        total_recipients: networksRes.data.total_active || 0,
      });
    } catch (error) {
      console.error("Failed to fetch email sources:", error);
    }
  }, [getAuthHeaders]);

  // Upload image for Free Newsletter
  const uploadFreeNewsletterImage = async (file, imageType = 'inline') => {
    try {
      setUploadingFreeImage(true);
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API_URL}/newsletter/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      
      if (response.data.url) {
        const imageNum = freeNewsletterForm.images.length + 1;
        const newImage = {
          url: response.data.url,
          type: imageType,
          placeholder: `IMAGE_${imageNum}`,
          filename: response.data.filename || file.name
        };
        setFreeNewsletterForm(prev => ({
          ...prev,
          images: [...prev.images, newImage]
        }));
        toast.success(`Image uploaded as ${imageType === 'inline' ? 'inline image' : 'attachment'}`);
      }
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingFreeImage(false);
    }
  };

  // Insert image placeholder at cursor position in content
  const insertImagePlaceholder = (imageIndex) => {
    const placeholder = `[IMAGE_${imageIndex + 1}]`;
    setFreeNewsletterForm(prev => ({
      ...prev,
      content: prev.content + `\n${placeholder}\n`
    }));
    // Mark image as inline if not already
    setFreeNewsletterForm(prev => ({
      ...prev,
      images: prev.images.map((img, idx) => 
        idx === imageIndex ? { ...img, type: 'inline' } : img
      )
    }));
    toast.success(`Placeholder ${placeholder} inserted. This image will appear inline in the email.`);
  };

  // Toggle image type between inline and attachment
  const toggleImageType = (imageIndex) => {
    setFreeNewsletterForm(prev => ({
      ...prev,
      images: prev.images.map((img, idx) => 
        idx === imageIndex 
          ? { ...img, type: img.type === 'inline' ? 'attachment' : 'inline' } 
          : img
      )
    }));
  };

  // Preview Free Newsletter
  const previewFreeNewsletter = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/newsletter/free/preview`, {
        subject: freeNewsletterForm.subject,
        content: freeNewsletterForm.content,
        images: freeNewsletterForm.images,
      }, getAuthHeaders());
      
      setFreeNewsletterPreview(response.data.html);
      setShowFreeNewsletterPreview(true);
    } catch (error) {
      toast.error("Failed to generate preview");
    } finally {
      setLoading(false);
    }
  };

  // Send Free Newsletter
  const sendFreeNewsletter = async (testEmail = null) => {
    try {
      setSendingFreeNewsletter(true);
      const payload = {
        subject: freeNewsletterForm.subject,
        content: freeNewsletterForm.content,
        images: freeNewsletterForm.images,
        target_networks: freeNewsletterForm.target_networks,
        target_tags: freeNewsletterForm.target_tags,
        target_sources: freeNewsletterForm.target_sources,
        test_email: testEmail,
      };
      
      const response = await axios.post(`${API_URL}/newsletter/free/send`, payload, getAuthHeaders());
      
      if (testEmail) {
        const attachInfo = response.data.attachments_included > 0 
          ? ` with ${response.data.attachments_included} attachment(s)` 
          : '';
        toast.success(`Test email sent to ${testEmail}${attachInfo}`);
      } else {
        const attachInfo = response.data.attachments_included > 0 
          ? ` (${response.data.attachments_included} attachments)` 
          : '';
        toast.success(`Newsletter sent to ${response.data.recipients_count} recipients${attachInfo}!`);
        // Reset form after successful send
        setFreeNewsletterForm({
          subject: "",
          content: "",
          images: [],
          target_sources: [],
          target_networks: [],
          target_tags: [],
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send newsletter");
    } finally {
      setSendingFreeNewsletter(false);
    }
  };

  // Get estimated recipient count for Free Newsletter
  const getEstimatedRecipients = async () => {
    try {
      const params = new URLSearchParams();
      if (freeNewsletterForm.target_networks.length > 0) {
        params.append("networks", freeNewsletterForm.target_networks.join(","));
      }
      if (freeNewsletterForm.target_tags.length > 0) {
        params.append("tags", freeNewsletterForm.target_tags.join(","));
      }
      if (freeNewsletterForm.target_sources.length > 0) {
        params.append("sources", freeNewsletterForm.target_sources.join(","));
      }
      
      const response = await axios.get(`${API_URL}/newsletter/free/estimate-recipients?${params}`, getAuthHeaders());
      return response.data.count || 0;
    } catch (error) {
      return 0;
    }
  };

  // Generate AI Newsletter Versions
  const generateAiNewsletterVersions = async () => {
    if (!freeNewsletterForm.content || freeNewsletterForm.content.trim().length < 20) {
      toast.error("Please add some content first (at least 20 characters)");
      return;
    }
    
    try {
      setAiGenerating(true);
      setAiRefinementRound(1);
      const response = await axios.post(`${API_URL}/newsletter/free/ai-generate`, {
        content: freeNewsletterForm.content,
        subject: freeNewsletterForm.subject,
        images: freeNewsletterForm.images,
      }, getAuthHeaders());
      
      setAiVersions(response.data.versions || []);
      setSelectedAiVersion(null);
      setShowAiVersionsModal(true);
      toast.success(`Generated ${response.data.versions?.length || 0} newsletter versions!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate AI versions");
    } finally {
      setAiGenerating(false);
    }
  };

  // Refine selected version (generate 3 more variations)
  const refineAiVersion = async (version) => {
    try {
      setAiGenerating(true);
      const response = await axios.post(`${API_URL}/newsletter/free/ai-refine`, {
        selected_content: version.content,
        selected_style: version.style,
        subject: version.subject,
        images: freeNewsletterForm.images,
      }, getAuthHeaders());
      
      setAiVersions(response.data.versions || []);
      setAiRefinementRound(response.data.refinement_round || 2);
      setSelectedAiVersion(null);
      toast.success(`Generated ${response.data.versions?.length || 0} refined versions!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refine versions");
    } finally {
      setAiGenerating(false);
    }
  };

  // Select an AI version
  const selectAiVersion = (version) => {
    setFreeNewsletterForm(prev => ({
      ...prev,
      subject: version.subject,
      content: version.content,
    }));
    setSelectedAiVersion(version);
    setShowAiVersionsModal(false);
    toast.success(`Selected "${version.style}" version - Ready to preview or send!`);
  };

  // Fetch sent newsletters archive
  const fetchSentNewsletters = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/newsletter/free/archive?page=${sentNewslettersPage}&page_size=10`,
        getAuthHeaders()
      );
      setSentNewsletters(response.data.newsletters || []);
      setSentNewslettersTotal(response.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch sent newsletters:", error);
    }
  }, [sentNewslettersPage, getAuthHeaders]);

  // View archive newsletter detail
  const viewArchiveNewsletter = async (newsletter) => {
    try {
      const response = await axios.get(
        `${API_URL}/newsletter/free/archive/${newsletter.sent_at}`,
        getAuthHeaders()
      );
      setSelectedArchiveNewsletter(response.data);
      setShowArchiveDetailModal(true);
    } catch (error) {
      // Fallback: use the newsletter directly
      setSelectedArchiveNewsletter(newsletter);
      setShowArchiveDetailModal(true);
    }
  };

  // Copy newsletter content to editor
  const copyNewsletterToEditor = (newsletter) => {
    setFreeNewsletterForm(prev => ({
      ...prev,
      subject: newsletter.subject || "",
      content: newsletter.content || "",
    }));
    toast.success("Newsletter content copied to editor!");
  };

  // Fetch suppression list
  const fetchSuppressionList = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (suppressionSearch) params.append("search", suppressionSearch);
      if (suppressionFilter && suppressionFilter !== "all") params.append("reason", suppressionFilter);
      
      const response = await axios.get(
        `${API_URL}/newsletter/suppression-list?${params}`,
        getAuthHeaders()
      );
      setSuppressionList(response.data.entries || []);
      setSuppressionStats(response.data.stats || {});
    } catch (error) {
      console.error("Failed to fetch suppression list:", error);
    }
  }, [suppressionSearch, suppressionFilter, getAuthHeaders]);

  // Add email to suppression list
  const addToSuppressionList = async () => {
    if (!newSuppressionEmail) {
      toast.error("Please enter an email address");
      return;
    }
    try {
      await axios.post(
        `${API_URL}/newsletter/suppression-list/add`,
        { email: newSuppressionEmail, reason: newSuppressionReason, source: "admin" },
        getAuthHeaders()
      );
      toast.success("Email added to suppression list");
      setNewSuppressionEmail("");
      setShowAddSuppressionModal(false);
      fetchSuppressionList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add email");
    }
  };

  // Remove from suppression list
  const removeFromSuppressionList = async (email) => {
    if (!window.confirm(`Remove ${email} from suppression list? They will receive emails again.`)) return;
    try {
      await axios.delete(
        `${API_URL}/newsletter/suppression-list/${encodeURIComponent(email)}`,
        getAuthHeaders()
      );
      toast.success("Email removed from suppression list");
      fetchSuppressionList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove email");
    }
  };

  // Fetch imported email list (from Import/Export page)
  const fetchImportedEmails = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (importedEmailSearch) params.append("search", importedEmailSearch);
      if (selectedImportedTag) params.append("tag", selectedImportedTag);
      
      const response = await axios.get(`${API_URL}/newsletter/email-lists/imported/all?${params}`, getAuthHeaders());
      setImportedEmails(response.data.emails || []);
      setImportedEmailsTotal(response.data.total || 0);
      setImportedEmailTags(response.data.available_tags || []);
    } catch (error) {
      console.error("Failed to fetch imported emails:", error);
    }
  }, [importedEmailSearch, selectedImportedTag, getAuthHeaders]);

  // Fetch all emails (unified newsletter_emails collection)
  const fetchAllEmails = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (allEmailsFilter.status) params.append("status", allEmailsFilter.status);
      if (allEmailsFilter.network) params.append("network", allEmailsFilter.network);
      if (allEmailsFilter.tag) params.append("tag", allEmailsFilter.tag);
      if (allEmailsFilter.search) params.append("search", allEmailsFilter.search);
      
      const response = await axios.get(`${API_URL}/newsletter/email-lists/all?${params}`, getAuthHeaders());
      setAllEmails(response.data.emails || []);
      setAllEmailsTotal(response.data.total || 0);
      setAllEmailsStats(response.data.stats || { total: 0, active: 0, bounced: 0, unsubscribed: 0 });
      setAllEmailsNetworks(response.data.networks || []);
      setAllEmailsTags(response.data.tags || []);
    } catch (error) {
      console.error("Failed to fetch all emails:", error);
    }
  }, [allEmailsFilter, getAuthHeaders]);

  // Sync emails from all sources
  const syncEmailLists = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${API_URL}/newsletter/email-lists/sync`, {
        sources: ["crm_contacts", "crm_companies", "newsletter_subscribers", "email_archives"]
      }, getAuthHeaders());
      toast.success(response.data.message);
      fetchAllEmails();
      fetchEmailStats();
    } catch (error) {
      toast.error("Failed to sync email lists");
    } finally {
      setSyncing(false);
    }
  };

  // Clean bounced emails
  const cleanBouncedEmails = async () => {
    if (!window.confirm("This will remove all bounced emails from the list. Continue?")) return;
    try {
      const response = await axios.post(`${API_URL}/newsletter/email-lists/clean-bounced`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchAllEmails();
      fetchEmailStats();
    } catch (error) {
      toast.error("Failed to clean bounced emails");
    }
  };

  // Send newsletter in batches
  const sendNewsletterBatch = async () => {
    try {
      setSendingBatch(true);
      const params = new URLSearchParams({
        batch_size: batchSendConfig.batchSize.toString(),
        delay_between_emails: batchSendConfig.delayBetweenEmails.toString(),
        delay_between_batches: batchSendConfig.delayBetweenBatches.toString(),
        exclude_recently_sent_days: batchSendConfig.excludeRecentDays.toString()
      });
      
      if (batchSendConfig.targetTags.length > 0) {
        params.append("target_tags", batchSendConfig.targetTags.join(","));
      }
      if (batchSendConfig.targetNetworks.length > 0) {
        params.append("target_networks", batchSendConfig.targetNetworks.join(","));
      }
      
      const response = await axios.post(
        `${API_URL}/newsletter/drafts/${batchSendConfig.draftId}/send-batch?${params}`,
        {},
        getAuthHeaders()
      );
      
      toast.success(response.data.message);
      setShowBatchSendDialog(false);
      
      // Start polling for progress
      pollSendProgress(batchSendConfig.draftId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start batch send");
    } finally {
      setSendingBatch(false);
    }
  };

  // Poll send progress
  const pollSendProgress = async (draftId) => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_URL}/newsletter/drafts/${draftId}/send-status`, getAuthHeaders());
        setSendProgress(response.data);
        
        if (response.data.status === "sent") {
          clearInterval(interval);
          toast.success("Newsletter sent successfully!");
          fetchDrafts();
        }
      } catch (error) {
        clearInterval(interval);
      }
    }, 5000);
    
    // Stop after 30 mins max
    setTimeout(() => clearInterval(interval), 1800000);
  };

  // Open batch send dialog
  const openBatchSend = (draftId) => {
    setBatchSendConfig({
      ...batchSendConfig,
      draftId: draftId,
      targetTags: [],
      targetNetworks: []
    });
    setShowBatchSendDialog(true);
  };

  // Sync offer inbox
  const syncOfferInbox = async (maxEmails = 1000) => {
    try {
      setOfferSyncing(true);
      await axios.post(`${API_URL}/offer-archive/sync?max_emails=${maxEmails}&batch_size=200`, {}, getAuthHeaders());
      toast.success(`Syncing ${maxEmails} emails from offer@europacific.com in background. This may take several minutes.`);
      
      // Poll for updates
      const pollInterval = setInterval(async () => {
        try {
          const response = await axios.get(`${API_URL}/offer-archive/stats`, getAuthHeaders());
          setOfferSyncStats(response.data);
          if (response.data.last_sync) {
            clearInterval(pollInterval);
            setOfferSyncing(false);
            fetchOfferRepository();
            toast.success("Offer inbox sync complete!");
          }
        } catch (e) {}
      }, 10000);
      
      // Clear after 10 minutes max
      setTimeout(() => {
        clearInterval(pollInterval);
        setOfferSyncing(false);
      }, 600000);
    } catch (error) {
      toast.error("Failed to start sync");
      setOfferSyncing(false);
    }
  };

  // Export offer repository
  const exportOfferRepository = async (format = "csv") => {
    try {
      const response = await axios.post(`${API_URL}/offer-archive/email-repository/export?format=${format}`, {}, getAuthHeaders());
      
      if (format === "csv") {
        const blob = new Blob([response.data.csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `offer_email_repository_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(response.data.emails, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `offer_email_repository_${new Date().toISOString().split("T")[0]}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      
      toast.success(`Exported ${response.data.count} emails`);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  // Fetch drafts
  const fetchDrafts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/newsletter/drafts`, getAuthHeaders());
      setDrafts(response.data.drafts || []);
    } catch (error) {
      console.error("Failed to fetch drafts:", error);
    }
  }, [getAuthHeaders]);

  // Import emails
  const handleImportEmails = async () => {
    if (!importNetwork || !importEmails.trim()) {
      toast.error("Please select a network and enter emails");
      return;
    }
    
    try {
      setLoading(true);
      // Parse emails - support comma, semicolon, newline separated
      const emailList = importEmails
        .split(/[,;\n]+/)
        .map(e => e.trim())
        .filter(e => e.includes("@"))
        .map(email => ({ email }));
      
      const response = await axios.post(`${API_URL}/newsletter/email-lists/import`, {
        network_name: importNetwork,
        emails: emailList
      }, getAuthHeaders());
      
      toast.success(`Imported ${response.data.imported} emails (${response.data.duplicates} duplicates)`);
      setShowImportDialog(false);
      setImportEmails("");
      fetchEmailStats();
    } catch (error) {
      toast.error("Failed to import emails");
    } finally {
      setLoading(false);
    }
  };

  // Update email status
  const updateEmailStatus = async (email, status) => {
    try {
      await axios.put(`${API_URL}/newsletter/email-lists/status`, { email, status }, getAuthHeaders());
      toast.success(`Email marked as ${status}`);
      fetchNetworkEmails(selectedNetwork);
      fetchEmailStats();
    } catch (error) {
      toast.error("Failed to update email status");
    }
  };

  // Create/Update draft
  const saveDraft = async () => {
    try {
      setLoading(true);
      
      if (editingDraft) {
        await axios.put(`${API_URL}/newsletter/drafts/${editingDraft._id}`, draftForm, getAuthHeaders());
        toast.success("Draft updated");
      } else {
        await axios.post(`${API_URL}/newsletter/drafts`, draftForm, getAuthHeaders());
        toast.success("Draft created");
      }
      
      setShowDraftEditor(false);
      setEditingDraft(null);
      fetchDrafts();
    } catch (error) {
      toast.error("Failed to save draft");
    } finally {
      setLoading(false);
    }
  };

  // Request approval
  const requestApproval = async (draftId) => {
    try {
      await axios.post(`${API_URL}/newsletter/drafts/${draftId}/request-approval?user_email=${user?.email}`, {}, getAuthHeaders());
      toast.success("Approval requested. CEO and Sales team have been notified.");
      fetchDrafts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request approval");
    }
  };

  // Approve draft
  const approveDraft = async (draftId) => {
    try {
      await axios.post(`${API_URL}/newsletter/drafts/${draftId}/approve?user_email=${user?.email}`, {}, getAuthHeaders());
      toast.success("Newsletter approved and ready to send!");
      fetchDrafts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const deleteDraft = async (draftId) => {
    if (!window.confirm("Are you sure you want to delete this newsletter draft?")) return;
    try {
      await axios.delete(`${API_URL}/newsletter/drafts/${draftId}`, getAuthHeaders());
      toast.success("Newsletter draft deleted");
      fetchDrafts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete draft");
    }
  };

  // Preview newsletter
  const previewNewsletter = async (draftId) => {
    try {
      setLoading(true);
      // First check if draft has html_content directly (Korea LCL drafts)
      const draft = drafts.find(d => d._id === draftId);
      if (draft && draft.html_content) {
        setPreviewHtml(draft.html_content);
        setShowPreview(true);
        return;
      }
      // Otherwise fetch from API
      const response = await axios.get(`${API_URL}/newsletter/drafts/${draftId}/preview`, getAuthHeaders());
      setPreviewHtml(response.data.html);
      setShowPreview(true);
    } catch (error) {
      toast.error("Failed to generate preview");
    } finally {
      setLoading(false);
    }
  };

  // Send newsletter
  const sendNewsletter = async (draftId, testEmail = null) => {
    try {
      setLoading(true);
      const params = testEmail ? `?test_email=${testEmail}` : "";
      await axios.post(`${API_URL}/newsletter/drafts/${draftId}/send${params}`, {}, getAuthHeaders());
      
      if (testEmail) {
        toast.success(`Test email sent to ${testEmail}`);
      } else {
        toast.success("Newsletter sending started! Check back later for status.");
      }
      fetchDrafts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send newsletter");
    } finally {
      setLoading(false);
    }
  };

  // Add article to draft section
  const addArticleToDraft = (articleId, sectionIndex) => {
    const newSections = [...draftForm.sections];
    if (!newSections[sectionIndex].articles.includes(articleId)) {
      newSections[sectionIndex].articles.push(articleId);
      setDraftForm({ ...draftForm, sections: newSections });
      toast.success("Article added to section");
    }
  };

  // Remove article from draft section
  const removeArticleFromDraft = (articleId, sectionIndex) => {
    const newSections = [...draftForm.sections];
    newSections[sectionIndex].articles = newSections[sectionIndex].articles.filter(id => id !== articleId);
    setDraftForm({ ...draftForm, sections: newSections });
  };

  // Open draft editor
  const openDraftEditor = (draft = null) => {
    if (draft) {
      setEditingDraft(draft);
      setDraftForm({
        subject: draft.subject || "",
        greeting: draft.greeting || "Dear Valued Logistics Partner,",
        sections: draft.sections || JSON.parse(JSON.stringify(DEFAULT_SECTIONS)),
        cta_text: draft.cta_text || "",
        cta_email: draft.cta_email || "info@europacific.com",
        cta_website: draft.cta_website || "www.europacific.com",
        target_lists: draft.target_lists || [],
        scheduled_date: draft.scheduled_date || "",
      });
    } else {
      setEditingDraft(null);
      setDraftForm({
        subject: `[EUROPACIFIC] Logistics & Supply Chain Newsletter — ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
        greeting: "Dear Valued Logistics Partner,",
        sections: JSON.parse(JSON.stringify(DEFAULT_SECTIONS)),
        cta_text: "Contact our sales team today for updated rate levels and sailing schedules",
        cta_email: "info@europacific.com",
        cta_website: "www.europacific.com",
        target_lists: [],
        scheduled_date: "",
      });
    }
    setShowDraftEditor(true);
  };

  // Effects
  useEffect(() => {
    fetchArticles();
    fetchEmailStats();
    fetchDrafts();
    fetchKoreaLCLContacts();
    fetchKoreaLCLTargets();
    fetchAllEmails();
    fetchNewsletterStats();
    fetchSentNewsletters();
    fetchSuppressionList();
  }, []);

  // Refresh sent newsletters when page changes
  useEffect(() => {
    fetchSentNewsletters();
  }, [sentNewslettersPage]);

  // Refresh suppression list when search/filter changes
  useEffect(() => {
    fetchSuppressionList();
  }, [suppressionSearch, suppressionFilter]);

  useEffect(() => {
    fetchArticles();
  }, [articleFilter, fetchArticles]);

  useEffect(() => {
    if (selectedNetwork) {
      fetchNetworkEmails(selectedNetwork);
    }
  }, [selectedNetwork, emailSearch, fetchNetworkEmails]);

  // Fetch all emails when filter changes
  useEffect(() => {
    fetchAllEmails();
  }, [allEmailsFilter, fetchAllEmails]);

  // Fetch offer repository when on emails tab
  useEffect(() => {
    if (activeTab === "emails") {
      fetchOfferRepository();
      fetchOfferSyncStats();
      fetchImportedEmails();
    }
  }, [activeTab, fetchOfferRepository, fetchOfferSyncStats, fetchImportedEmails]);

  // Fetch imported emails when search or tag changes
  useEffect(() => {
    if (activeTab === "emails") {
      fetchImportedEmails();
    }
  }, [importedEmailSearch, selectedImportedTag, fetchImportedEmails, activeTab]);

  // ============== KOREA LCL FUNCTIONS ==============
  
  // Fetch Korea LCL contacts
  const fetchKoreaLCLContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/newsletter/korea-lcl/contacts`, getAuthHeaders());
      setKoreaLCLContacts(response.data);
    } catch (error) {
      console.error("Failed to fetch Korea LCL contacts:", error);
    }
  };

  // Fetch Korea target companies
  const fetchKoreaLCLTargets = async () => {
    try {
      const response = await axios.get(`${API_URL}/newsletter/korea-lcl/target-companies?tags=Korea`, getAuthHeaders());
      setKoreaLCLTargets(response.data);
    } catch (error) {
      console.error("Failed to fetch Korea targets:", error);
    }
  };

  // Handle PDF upload for Korea LCL
  const handleKoreaLCLPDFUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Please upload a PDF file");
      return;
    }
    
    setUploadingPDF(true);
    try {
      // Create form data for file upload
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload and parse PDF
      const response = await axios.post(
        `${API_URL}/newsletter/korea-lcl/upload-schedule`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success && response.data.schedule_items?.length > 0) {
        // Add parsed items to schedule
        setKoreaLCLSchedule(response.data.schedule_items);
        toast.success(`Extracted ${response.data.schedule_items.length} sailings from PDF`);
      } else {
        toast.warning("No Koper sailings found in PDF. Please add manually.");
      }
      
    } catch (error) {
      console.error("PDF upload error:", error);
      toast.error("Failed to parse PDF. Please add schedule data manually.");
    } finally {
      setUploadingPDF(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // Handle image upload for Korea LCL schedule (uses AI Vision)
  const handleKoreaLCLImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload an image file (PNG, JPEG, or WEBP)");
      return;
    }
    
    setUploadingImage(true);
    try {
      // Create form data for file upload
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload and extract data using AI Vision
      const response = await axios.post(
        `${API_URL}/newsletter/korea-lcl/upload-image`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success && response.data.schedule_items?.length > 0) {
        // Add extracted items to schedule
        setKoreaLCLSchedule(response.data.schedule_items);
        toast.success(`Extracted ${response.data.schedule_items.length} sailings from image`);
      } else {
        toast.warning("No schedule data found in image. Please add manually.");
      }
      
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to extract data from image. Please add manually.");
    } finally {
      setUploadingImage(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // Add Korea LCL schedule item
  const addKoreaLCLScheduleItem = () => {
    setKoreaLCLSchedule([...koreaLCLSchedule, {
      vessel_name: "",
      voyage: "",
      doc_closing: "",
      cargo_closing: "",
      busan_etd: "",
      koper_eta: "",
      shipping_line: "CMA CGM"
    }]);
  };

  // Update Korea LCL schedule item
  const updateKoreaLCLScheduleItem = (index, field, value) => {
    const newSchedule = [...koreaLCLSchedule];
    newSchedule[index][field] = value;
    setKoreaLCLSchedule(newSchedule);
  };

  // Remove Korea LCL schedule item
  const removeKoreaLCLScheduleItem = (index) => {
    setKoreaLCLSchedule(koreaLCLSchedule.filter((_, i) => i !== index));
  };

  // Preview Korea LCL newsletter
  const previewKoreaLCLNewsletter = async () => {
    if (koreaLCLSchedule.length === 0) {
      toast.error("Please add at least one sailing to preview");
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/newsletter/korea-lcl/preview`, koreaLCLSchedule, getAuthHeaders());
      setKoreaLCLPreview(response.data.html);
      setShowKoreaLCLPreview(true);
    } catch (error) {
      toast.error("Failed to generate preview");
    } finally {
      setLoading(false);
    }
  };

  // Create Korea LCL draft
  const createKoreaLCLDraft = async () => {
    if (koreaLCLSchedule.length === 0) {
      toast.error("Please add at least one sailing");
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/newsletter/korea-lcl/create-draft`, koreaLCLSchedule, getAuthHeaders());
      toast.success(`Draft created! ${response.data.recipient_count} recipients from Korea-tagged companies`);
      fetchDrafts();
      setActiveTab("newsletters");
    } catch (error) {
      toast.error("Failed to create draft");
    } finally {
      setLoading(false);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const configs = {
      draft: { icon: Edit, color: "bg-gray-100 text-gray-700", label: "Draft" },
      pending_approval: { icon: Clock, color: "bg-yellow-100 text-yellow-700", label: "Pending Approval" },
      approved: { icon: CheckCircle, color: "bg-green-100 text-green-700", label: "Approved" },
      sending: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: "Sending..." },
      sent: { icon: MailCheck, color: "bg-emerald-100 text-emerald-700", label: "Sent" },
    };
    const config = configs[status] || configs.draft;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className={`h-3 w-3 ${status === 'sending' ? 'animate-spin' : ''}`} />
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <main className="container mx-auto px-4 py-6 pt-20">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Newspaper className="h-6 w-6 text-blue-600" />
              Newsletter Management
            </h1>
            <p className="text-slate-600">Manage industry articles, email lists, and send newsletters</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="free-newsletter" className="gap-2">
              <Mail className="h-4 w-4" />
              Free Newsletter
            </TabsTrigger>
            <TabsTrigger value="articles" className="gap-2">
              <Globe className="h-4 w-4" />
              Industry Articles
            </TabsTrigger>
            <TabsTrigger value="korea-lcl" className="gap-2">
              <span className="text-lg">🇰🇷</span>
              Korea LCL
            </TabsTrigger>
            <TabsTrigger value="emails" className="gap-2">
              <Users className="h-4 w-4" />
              Email Lists
            </TabsTrigger>
            <TabsTrigger value="drafts" className="gap-2">
              <FileText className="h-4 w-4" />
              Newsletters
            </TabsTrigger>
            <TabsTrigger value="road-closures" className="gap-2" data-testid="road-closures-tab">
              <AlertCircle className="h-4 w-4" />
              Road Closures
            </TabsTrigger>
          </TabsList>

          {/* FREE NEWSLETTER TAB */}
          <TabsContent value="free-newsletter" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Editor */}
              <div className="lg:col-span-2 space-y-4">
                <div className="bg-white rounded-lg border p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-blue-600" />
                    Compose Newsletter
                  </h2>
                  
                  {/* Subject */}
                  <div className="space-y-2 mb-4">
                    <Label>Subject Line</Label>
                    <Input
                      placeholder="e.g., [EUROPACIFIC] Track & Trace Platform Now Live"
                      value={freeNewsletterForm.subject}
                      onChange={(e) => setFreeNewsletterForm({...freeNewsletterForm, subject: e.target.value})}
                      data-testid="free-newsletter-subject"
                    />
                  </div>
                  
                  {/* Content Editor */}
                  <div className="space-y-2 mb-4">
                    <Label>Newsletter Content (Markdown supported)</Label>
                    <Textarea
                      placeholder="Write your newsletter content here...

You can use **bold** and *italic* text.

### Headers like this

- Bullet points
- Lists

Links: [Click here](https://www.europacific.com)"
                      value={freeNewsletterForm.content}
                      onChange={(e) => setFreeNewsletterForm({...freeNewsletterForm, content: e.target.value})}
                      className="min-h-[400px] font-mono text-sm"
                      data-testid="free-newsletter-content"
                    />
                    <p className="text-xs text-slate-500">
                      Tip: Use Markdown for formatting. **bold**, *italic*, ### headers, - lists, [link](url)
                    </p>
                  </div>
                  
                  {/* Image Upload */}
                  <div className="space-y-2 mb-4">
                    <Label className="flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" />
                      Images
                    </Label>
                    <p className="text-xs text-slate-500">
                      Upload images and choose to insert them inline (within text) or as attachments
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => document.getElementById('free-newsletter-image-upload').click()}
                        disabled={uploadingFreeImage}
                        data-testid="free-newsletter-upload-btn"
                      >
                        {uploadingFreeImage ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        Upload Image
                      </Button>
                      <input
                        id="free-newsletter-image-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            uploadFreeNewsletterImage(e.target.files[0], 'inline');
                          }
                        }}
                      />
                      <span className="text-sm text-slate-500">
                        {freeNewsletterForm.images.length} image(s) uploaded
                      </span>
                    </div>
                    
                    {/* Show uploaded images with inline/attachment options */}
                    {freeNewsletterForm.images.length > 0 && (
                      <div className="space-y-2 mt-3">
                        {freeNewsletterForm.images.map((img, idx) => {
                          const imageUrl = typeof img === 'string' ? img : img.url;
                          const imageType = typeof img === 'string' ? 'inline' : (img.type || 'inline');
                          const placeholder = `[IMAGE_${idx + 1}]`;
                          
                          return (
                            <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border">
                              <img 
                                src={imageUrl} 
                                alt={`Upload ${idx + 1}`} 
                                className="h-16 w-16 object-cover rounded border"
                              />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-slate-700">Image {idx + 1}</p>
                                <p className="text-xs text-slate-500 font-mono">{placeholder}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    imageType === 'inline' 
                                      ? 'bg-blue-100 text-blue-700' 
                                      : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {imageType === 'inline' ? '📷 Inline' : '📎 Attachment'}
                                  </span>
                                </div>
                              </div>
                              <div className="flex flex-col gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => insertImagePlaceholder(idx)}
                                  className="text-xs"
                                  title="Insert placeholder at cursor position"
                                >
                                  Insert {placeholder}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => toggleImageType(idx)}
                                  className="text-xs"
                                >
                                  {imageType === 'inline' ? 'Make Attachment' : 'Make Inline'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-xs text-red-600 hover:text-red-700"
                                  onClick={() => {
                                    setFreeNewsletterForm(prev => ({
                                      ...prev,
                                      images: prev.images.filter((_, i) => i !== idx)
                                    }));
                                  }}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Help text */}
                        <div className="text-xs text-slate-500 bg-blue-50 p-2 rounded mt-2">
                          <strong>💡 How to use:</strong>
                          <ul className="mt-1 space-y-1 list-disc list-inside">
                            <li><strong>Inline:</strong> Click "Insert [IMAGE_X]" to add placeholder in your text. Image appears where you place it.</li>
                            <li><strong>Attachment:</strong> Click "Make Attachment" to send as email attachment instead.</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* AI Generate Button */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2 text-purple-700">
                    <Sparkles className="h-4 w-4" />
                    AI Newsletter Generator
                  </h3>
                  <p className="text-xs text-slate-600 mb-3">
                    Let AI create 3 professional versions of your newsletter based on your text and images
                  </p>
                  <Button
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={generateAiNewsletterVersions}
                    disabled={aiGenerating || !freeNewsletterForm.content || freeNewsletterForm.content.length < 20}
                    data-testid="free-newsletter-ai-generate"
                  >
                    {aiGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating 3 Versions...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate AI Versions
                      </>
                    )}
                  </Button>
                  {freeNewsletterForm.content && freeNewsletterForm.content.length < 20 && (
                    <p className="text-xs text-orange-600 mt-2">Add at least 20 characters of content</p>
                  )}
                </div>
              </div>
              
              {/* Recipients & Actions Sidebar */}
              <div className="space-y-4">
                {/* Recipients Selection */}
                <div className="bg-white rounded-lg border p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    Select Recipients
                  </h3>
                  
                  {/* Newsletter Networks */}
                  <div className="mb-4">
                    <Label className="text-sm text-slate-600 mb-2 block">Newsletter Networks</Label>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {["CRM", "EMAIL_ARCHIVE", "OFFERS", "WFN", "TWIG", "UOL", "GAL", "XLP", "SBC", "KOREA"].map(network => (
                        <label key={network} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={freeNewsletterForm.target_networks.includes(network)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFreeNewsletterForm(prev => ({
                                  ...prev,
                                  target_networks: [...prev.target_networks, network]
                                }));
                              } else {
                                setFreeNewsletterForm(prev => ({
                                  ...prev,
                                  target_networks: prev.target_networks.filter(n => n !== network)
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          <span className={`px-2 py-0.5 rounded text-xs ${NETWORK_COLORS[network] || 'bg-gray-100'}`}>
                            {network}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Additional Sources */}
                  <div className="mb-4">
                    <Label className="text-sm text-slate-600 mb-2 block">Additional Sources</Label>
                    <div className="space-y-1">
                      {[
                        { id: "crm_contacts", label: "CRM Contacts" },
                        { id: "customers", label: "PF Customers" },
                        { id: "offer_archive", label: "Offer Archive" },
                      ].map(source => (
                        <label key={source.id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={freeNewsletterForm.target_sources.includes(source.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFreeNewsletterForm(prev => ({
                                  ...prev,
                                  target_sources: [...prev.target_sources, source.id]
                                }));
                              } else {
                                setFreeNewsletterForm(prev => ({
                                  ...prev,
                                  target_sources: prev.target_sources.filter(s => s !== source.id)
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          {source.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* CRM Tags */}
                  <div className="mb-4">
                    <Label className="text-sm text-slate-600 mb-2 block">CRM Company Tags</Label>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {["Korea", "Slovenia", "Adriatic", "Partner", "Agent", "Customer", "Carrier"].map(tag => (
                        <label key={tag} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={freeNewsletterForm.target_tags.includes(tag)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFreeNewsletterForm(prev => ({
                                  ...prev,
                                  target_tags: [...prev.target_tags, tag]
                                }));
                              } else {
                                setFreeNewsletterForm(prev => ({
                                  ...prev,
                                  target_tags: prev.target_tags.filter(t => t !== tag)
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          {tag}
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="text-sm text-slate-600 bg-slate-50 p-2 rounded">
                    <strong>Selected:</strong> {freeNewsletterForm.target_networks.length} networks, {freeNewsletterForm.target_sources.length} sources, {freeNewsletterForm.target_tags.length} tags
                  </div>
                </div>
                
                {/* Actions */}
                <div className="bg-white rounded-lg border p-4 space-y-3">
                  <h3 className="font-semibold mb-2">Actions</h3>
                  
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={previewFreeNewsletter}
                    disabled={!freeNewsletterForm.subject || !freeNewsletterForm.content || loading}
                    data-testid="free-newsletter-preview-btn"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview Newsletter
                  </Button>
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Test email"
                      id="free-newsletter-test-email"
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      onClick={() => {
                        const testEmail = document.getElementById('free-newsletter-test-email').value;
                        if (testEmail) sendFreeNewsletter(testEmail);
                        else toast.error("Enter a test email");
                      }}
                      disabled={sendingFreeNewsletter || !freeNewsletterForm.subject || !freeNewsletterForm.content}
                      data-testid="free-newsletter-test-btn"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => sendFreeNewsletter()}
                    disabled={
                      sendingFreeNewsletter || 
                      !freeNewsletterForm.subject || 
                      !freeNewsletterForm.content ||
                      (freeNewsletterForm.target_networks.length === 0 && 
                       freeNewsletterForm.target_sources.length === 0 &&
                       freeNewsletterForm.target_tags.length === 0)
                    }
                    data-testid="free-newsletter-send-btn"
                  >
                    {sendingFreeNewsletter ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Newsletter
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Sent Newsletter Archive */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Archive className="h-5 w-5" />
                  Sent History
                </h3>
                <Button variant="outline" size="sm" onClick={fetchSentNewsletters}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
              
              {sentNewsletters.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No newsletters sent yet</p>
              ) : (
                <div className="space-y-2">
                  {sentNewsletters.map((nl, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border hover:border-blue-300 transition-colors">
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{nl.subject || "Untitled Newsletter"}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <CalendarDays className="h-3 w-3" />
                            {new Date(nl.sent_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span className="flex items-center gap-1 text-green-600">
                            <MailCheck className="h-3 w-3" />
                            {nl.sent_count} sent
                          </span>
                          {nl.failed_count > 0 && (
                            <span className="flex items-center gap-1 text-red-500">
                              <MailX className="h-3 w-3" />
                              {nl.failed_count} failed
                            </span>
                          )}
                          {nl.suppressed_count > 0 && (
                            <span className="flex items-center gap-1 text-amber-500">
                              <Ban className="h-3 w-3" />
                              {nl.suppressed_count} suppressed
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => viewArchiveNewsletter(nl)} title="View full newsletter">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => copyNewsletterToEditor(nl)} title="Copy content to editor">
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {sentNewslettersTotal > 10 && (
                    <div className="flex justify-center gap-2 mt-4">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={sentNewslettersPage <= 1}
                        onClick={() => setSentNewslettersPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-slate-500 py-2">
                        Page {sentNewslettersPage} of {Math.ceil(sentNewslettersTotal / 10)}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={sentNewslettersPage >= Math.ceil(sentNewslettersTotal / 10)}
                        onClick={() => setSentNewslettersPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Email Suppression List */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <ShieldX className="h-5 w-5" />
                  Suppression List
                  <span className="text-xs font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                    {suppressionStats.total || 0} emails blocked
                  </span>
                </h3>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowAddSuppressionModal(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                  <Button variant="outline" size="sm" onClick={fetchSuppressionList}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Stats */}
              <div className="flex flex-wrap gap-3 mb-4 text-xs">
                <span className="px-2 py-1 bg-red-50 text-red-700 rounded">Hard Bounces: {suppressionStats.hard_bounces || 0}</span>
                <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded">Soft Bounces: {suppressionStats.soft_bounces || 0}</span>
                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">Unsubscribed: {suppressionStats.unsubscribed || 0}</span>
                <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded">SMTP Errors: {suppressionStats.smtp_errors || 0}</span>
                <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded">Manual: {suppressionStats.manual || 0}</span>
              </div>
              
              {/* Search and Filter */}
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Search email..."
                  value={suppressionSearch}
                  onChange={(e) => setSuppressionSearch(e.target.value)}
                  className="max-w-xs"
                  onKeyDown={(e) => e.key === 'Enter' && fetchSuppressionList()}
                />
                <Select value={suppressionFilter} onValueChange={setSuppressionFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All reasons" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All reasons</SelectItem>
                    <SelectItem value="hard_bounce">Hard Bounce</SelectItem>
                    <SelectItem value="soft_bounce">Soft Bounce</SelectItem>
                    <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                    <SelectItem value="smtp_error">SMTP Error</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={fetchSuppressionList}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              
              {/* List */}
              <div className="max-h-64 overflow-y-auto">
                {suppressionList.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No suppressed emails</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Email</th>
                        <th className="text-left p-2">Reason</th>
                        <th className="text-left p-2">Source</th>
                        <th className="text-left p-2">Date</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {suppressionList.map((entry, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="p-2 font-mono text-xs">{entry.email}</td>
                          <td className="p-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              entry.reason === 'hard_bounce' ? 'bg-red-100 text-red-700' :
                              entry.reason === 'soft_bounce' ? 'bg-amber-100 text-amber-700' :
                              entry.reason === 'unsubscribed' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {entry.reason?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="p-2 text-xs text-slate-500">{entry.source}</td>
                          <td className="p-2 text-xs text-slate-500">
                            {new Date(entry.suppressed_at).toLocaleDateString('en-GB')}
                          </td>
                          <td className="p-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => removeFromSuppressionList(entry.email)}
                              title="Remove from suppression list"
                            >
                              <X className="h-4 w-4 text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ARTICLES TAB */}
          <TabsContent value="articles" className="space-y-4">
            <div className="bg-white rounded-lg border p-4">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Button 
                    onClick={triggerScraping} 
                    disabled={scraping}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {scraping ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Scraping...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Scrape New Articles
                      </>
                    )}
                  </Button>
                  <span className="text-sm text-slate-500">
                    {articlesTotal} articles collected
                  </span>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={articleFilter.source} onValueChange={(v) => setArticleFilter({...articleFilter, source: v === "all" ? "" : v})}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="All Sources" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      {Object.entries(SOURCE_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={articleFilter.category} onValueChange={(v) => setArticleFilter({...articleFilter, category: v === "all" ? "" : v})}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="industry">Industry</SelectItem>
                      <SelectItem value="local_slovenia">Slovenia</SelectItem>
                      <SelectItem value="local_adriatic">Adriatic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Filter toggles row */}
              <div className="flex flex-wrap items-center gap-4 mb-4 pt-2 border-t">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={articleFilter.unused}
                    onChange={(e) => setArticleFilter({...articleFilter, unused: e.target.checked})}
                    className="rounded"
                  />
                  Unused only
                </label>
                
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={articleFilter.hideCompetitor}
                    onChange={(e) => setArticleFilter({...articleFilter, hideCompetitor: e.target.checked})}
                    className="rounded border-amber-400"
                  />
                  <span className="text-amber-700">Hide Competitor News</span>
                </label>
                
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={articleFilter.localFirst}
                    onChange={(e) => setArticleFilter({...articleFilter, localFirst: e.target.checked})}
                    className="rounded border-blue-400"
                  />
                  <span className="text-blue-700">Local First</span>
                </label>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : articles.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No articles found. Click "Scrape New Articles" to fetch from sources.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {articles
                    .filter(article => !articleFilter.hideCompetitor || !article.is_competitor_news)
                    .sort((a, b) => {
                      if (articleFilter.localFirst) {
                        // Local news first
                        const aLocal = a.is_local_news || a.category === 'local_slovenia' || a.category === 'local_adriatic';
                        const bLocal = b.is_local_news || b.category === 'local_slovenia' || b.category === 'local_adriatic';
                        if (aLocal && !bLocal) return -1;
                        if (!aLocal && bLocal) return 1;
                      }
                      return 0;
                    })
                    .map((article) => (
                    <div 
                      key={article._id} 
                      className={`border rounded-lg p-4 hover:bg-slate-50 transition-colors ${article.used_in_newsletter ? 'opacity-60' : ''} ${article.is_competitor_news ? 'border-l-4 border-l-amber-400' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`px-2 py-0.5 rounded text-xs text-white ${SOURCE_CONFIG[article.source_key]?.color || 'bg-gray-500'}`}>
                              {article.source_name}
                            </span>
                            {article.category !== 'industry' && (
                              <span className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700">
                                {article.category === 'local_slovenia' ? '🇸🇮 Slovenia' : '🏔️ Adriatic'}
                              </span>
                            )}
                            {article.is_local_news && article.category === 'industry' && (
                              <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                                🏔️ Local Content
                              </span>
                            )}
                            {article.is_competitor_news && (
                              <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">
                                ⚠️ Competitor
                              </span>
                            )}
                            {article.used_in_newsletter && (
                              <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">
                                Used
                              </span>
                            )}
                          </div>
                          <h3 className="font-semibold text-slate-900 mb-1">{article.title}</h3>
                          <p className="text-sm text-slate-600 line-clamp-2">{article.summary}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                            <span>{new Date(article.scraped_at).toLocaleDateString()}</span>
                            <a href={article.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                              View Source <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </div>
                        
                        {showDraftEditor && (
                          <Select onValueChange={(v) => addArticleToDraft(article._id, parseInt(v))}>
                            <SelectTrigger className="w-40">
                              <SelectValue placeholder="Add to section" />
                            </SelectTrigger>
                            <SelectContent>
                              {draftForm.sections.map((section, idx) => (
                                <SelectItem key={idx} value={idx.toString()}>
                                  {section.emoji} {section.title.substring(0, 20)}...
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* KOREA LCL TAB */}
          <TabsContent value="korea-lcl" className="space-y-4">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Left: Schedule Entry */}
              <div className="md:col-span-2 space-y-4">
                <div className="bg-white rounded-lg border p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        🇰🇷 Korea LCL Weekly Schedule
                      </h2>
                      <p className="text-sm text-slate-600">Enter sailing schedule for Korea → Koper service</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          onChange={handleKoreaLCLImageUpload}
                          className="hidden"
                          data-testid="korea-lcl-image-upload-input"
                        />
                        <Button variant="default" asChild disabled={uploadingImage} data-testid="upload-image-btn">
                          <span>
                            {uploadingImage ? (
                              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing...</>
                            ) : (
                              <><ImageIcon className="h-4 w-4 mr-2" /> Upload Image</>
                            )}
                          </span>
                        </Button>
                      </label>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={handleKoreaLCLPDFUpload}
                          className="hidden"
                        />
                        <Button variant="outline" asChild disabled={uploadingPDF}>
                          <span>
                            {uploadingPDF ? (
                              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                            ) : (
                              <><Upload className="h-4 w-4 mr-2" /> Upload ECL PDF</>
                            )}
                          </span>
                        </Button>
                      </label>
                      <Button onClick={addKoreaLCLScheduleItem} variant="outline">
                        <Plus className="h-4 w-4 mr-2" /> Add Sailing
                      </Button>
                    </div>
                  </div>

                  {/* Schedule Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Vessel</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Voyage</th>
                          <th className="px-3 py-2 text-center font-medium text-slate-600">Doc CLG</th>
                          <th className="px-3 py-2 text-center font-medium text-slate-600">Cargo CLG</th>
                          <th className="px-3 py-2 text-center font-medium text-slate-600">BUS ETD</th>
                          <th className="px-3 py-2 text-center font-medium text-slate-600 text-blue-700">KOPER ETA</th>
                          <th className="px-3 py-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {koreaLCLSchedule.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="px-3 py-8 text-center text-slate-400">
                              No sailings added yet. Click "Add Sailing" or upload ECL PDF.
                            </td>
                          </tr>
                        ) : (
                          koreaLCLSchedule.map((item, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="px-3 py-2">
                                <Input
                                  value={item.vessel_name}
                                  onChange={(e) => updateKoreaLCLScheduleItem(idx, 'vessel_name', e.target.value)}
                                  placeholder="CMA CGM HERMES"
                                  className="h-8 text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  value={item.voyage}
                                  onChange={(e) => updateKoreaLCLScheduleItem(idx, 'voyage', e.target.value)}
                                  placeholder="0BEN7W1MA"
                                  className="h-8 text-sm font-mono"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  value={item.doc_closing}
                                  onChange={(e) => updateKoreaLCLScheduleItem(idx, 'doc_closing', e.target.value)}
                                  placeholder="2/24"
                                  className="h-8 text-sm text-center w-20"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  value={item.cargo_closing}
                                  onChange={(e) => updateKoreaLCLScheduleItem(idx, 'cargo_closing', e.target.value)}
                                  placeholder="2/25"
                                  className="h-8 text-sm text-center w-20"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  value={item.busan_etd}
                                  onChange={(e) => updateKoreaLCLScheduleItem(idx, 'busan_etd', e.target.value)}
                                  placeholder="3/3"
                                  className="h-8 text-sm text-center w-20"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  value={item.koper_eta}
                                  onChange={(e) => updateKoreaLCLScheduleItem(idx, 'koper_eta', e.target.value)}
                                  placeholder="4/12"
                                  className="h-8 text-sm text-center w-20 font-semibold text-blue-700"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeKoreaLCLScheduleItem(idx)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Action Buttons */}
                  {koreaLCLSchedule.length > 0 && (
                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                      <Button variant="outline" onClick={previewKoreaLCLNewsletter} disabled={loading}>
                        <Eye className="h-4 w-4 mr-2" /> Preview
                      </Button>
                      <Button onClick={createKoreaLCLDraft} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                        <Send className="h-4 w-4 mr-2" /> Create Draft
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Contacts & Recipients */}
              <div className="space-y-4">
                {/* Origin Agent */}
                {koreaLCLContacts && (
                  <>
                    <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
                      <h3 className="text-sm font-semibold text-amber-800 mb-2">🇰🇷 Origin Agent</h3>
                      <p className="font-medium text-slate-900">{koreaLCLContacts.origin_agent?.company}</p>
                      <p className="text-sm text-slate-600">{koreaLCLContacts.origin_agent?.address}</p>
                      <p className="text-sm text-slate-600">{koreaLCLContacts.origin_agent?.city}</p>
                      <div className="mt-2 pt-2 border-t border-amber-200">
                        <p className="text-sm"><strong>Contact:</strong> {koreaLCLContacts.origin_agent?.contact_name}</p>
                        <p className="text-sm"><strong>Phone:</strong> {koreaLCLContacts.origin_agent?.phone}</p>
                        <p className="text-sm"><strong>Email:</strong> {koreaLCLContacts.origin_agent?.email}</p>
                      </div>
                    </div>

                    {/* Destination Agent */}
                    <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                      <h3 className="text-sm font-semibold text-blue-800 mb-2">🇸🇮 Destination Agent</h3>
                      <p className="font-medium text-slate-900">{koreaLCLContacts.destination_agent?.company}</p>
                      <p className="text-sm text-slate-600">{koreaLCLContacts.destination_agent?.address}</p>
                      <p className="text-sm text-slate-600">{koreaLCLContacts.destination_agent?.city}</p>
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        <p className="text-sm"><strong>Contact:</strong> {koreaLCLContacts.destination_agent?.contact_name}</p>
                        <p className="text-sm"><strong>Phone:</strong> {koreaLCLContacts.destination_agent?.phone}</p>
                        <p className="text-sm"><strong>Email:</strong> {koreaLCLContacts.destination_agent?.email}</p>
                      </div>
                    </div>

                    {/* Warehouse */}
                    <div className="bg-green-50 rounded-lg border border-green-200 p-4">
                      <h3 className="text-sm font-semibold text-green-800 mb-2">🏭 Bonded Warehouse</h3>
                      <p className="font-medium text-slate-900">{koreaLCLContacts.bonded_warehouse?.company}</p>
                      <p className="text-sm text-slate-600">{koreaLCLContacts.bonded_warehouse?.address}, {koreaLCLContacts.bonded_warehouse?.city}</p>
                      <div className="mt-2 pt-2 border-t border-green-200">
                        <p className="text-sm"><strong>Contact:</strong> {koreaLCLContacts.bonded_warehouse?.contact_name}</p>
                        <p className="text-sm"><strong>Email:</strong> {koreaLCLContacts.bonded_warehouse?.email}</p>
                      </div>
                    </div>
                  </>
                )}

                {/* Target Recipients */}
                <div className="bg-white rounded-lg border p-4">
                  <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" /> Target Recipients
                  </h3>
                  <p className="text-2xl font-bold text-blue-600">{koreaLCLTargets.total}</p>
                  <p className="text-sm text-slate-600 mb-2">Korea-tagged companies</p>
                  <div className="text-xs text-slate-500 space-y-1">
                    <p>• CRM: {koreaLCLTargets.sources?.crm || 0}</p>
                    <p>• Offers: {koreaLCLTargets.sources?.offers || 0}</p>
                    <p>• Emails: {koreaLCLTargets.sources?.emails || 0}</p>
                  </div>
                  <Button 
                    variant="link" 
                    className="p-0 h-auto text-xs text-blue-600 mt-2"
                    onClick={() => setActiveTab("emails")}
                  >
                    Manage email lists →
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* EMAIL LISTS TAB */}
          <TabsContent value="emails" className="space-y-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Users className="h-4 w-4" />
                  <span className="text-sm">Total</span>
                </div>
                <p className="text-2xl font-bold text-blue-600">{allEmailsStats.total}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <MailCheck className="h-4 w-4" />
                  <span className="text-sm">Active</span>
                </div>
                <p className="text-2xl font-bold text-green-600">{allEmailsStats.active}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <MailX className="h-4 w-4" />
                  <span className="text-sm">Bounced</span>
                </div>
                <p className="text-2xl font-bold text-red-600">{allEmailsStats.bounced}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <UserX className="h-4 w-4" />
                  <span className="text-sm">Unsubscribed</span>
                </div>
                <p className="text-2xl font-bold text-orange-600">{allEmailsStats.unsubscribed}</p>
              </div>
              <div className="bg-white rounded-lg border p-4 flex flex-col gap-2">
                <Button 
                  onClick={syncEmailLists} 
                  disabled={syncing}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Sync All Sources
                </Button>
                <Button 
                  onClick={cleanBouncedEmails} 
                  variant="outline"
                  className="w-full text-red-600 border-red-200 hover:bg-red-50"
                  size="sm"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clean Bounced
                </Button>
              </div>
            </div>

            {/* All Emails Unified View */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-600" />
                  Newsletter Email List
                  <span className="text-xs text-slate-500 font-normal ml-2">
                    Synced from CRM, Companies & Imported Lists
                  </span>
                </h3>
                <div className="text-sm text-slate-500">
                  Showing {allEmails.length} of {allEmailsTotal}
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b">
                <div className="relative flex-1 min-w-[200px] max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search emails..."
                    value={allEmailsFilter.search}
                    onChange={(e) => setAllEmailsFilter({ ...allEmailsFilter, search: e.target.value })}
                    className="pl-9"
                  />
                </div>
                <Select 
                  value={allEmailsFilter.status || "all"} 
                  onValueChange={(v) => setAllEmailsFilter({ ...allEmailsFilter, status: v === "all" ? "" : v })}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="bounced">Bounced</SelectItem>
                    <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                  </SelectContent>
                </Select>
                <Select 
                  value={allEmailsFilter.network || "all"} 
                  onValueChange={(v) => setAllEmailsFilter({ ...allEmailsFilter, network: v === "all" ? "" : v })}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Network" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Networks</SelectItem>
                    {allEmailsNetworks.map(n => (
                      <SelectItem key={n} value={n}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={allEmailsFilter.tag || "all"} 
                  onValueChange={(v) => setAllEmailsFilter({ ...allEmailsFilter, tag: v === "all" ? "" : v })}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Tag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tags</SelectItem>
                    {allEmailsTags.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Email List Table */}
              <div className="max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr>
                      <th className="text-left p-2">Email</th>
                      <th className="text-left p-2">Name/Company</th>
                      <th className="text-left p-2">Networks</th>
                      <th className="text-left p-2">Tags</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Last Sent</th>
                      <th className="text-left p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allEmails.map((item, idx) => (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="p-2 font-mono text-xs">{item.email}</td>
                        <td className="p-2">
                          <div className="text-xs">
                            {item.name && <p className="font-medium">{item.name}</p>}
                            {item.company && <p className="text-slate-500">{item.company}</p>}
                          </div>
                        </td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {(item.networks || []).map((n, i) => (
                              <span key={i} className={`px-1.5 py-0.5 rounded text-xs ${NETWORK_COLORS[n] || 'bg-gray-100 text-gray-700'}`}>
                                {n}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {(item.tags || []).slice(0, 3).map((t, i) => (
                              <span key={i} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs">
                                {t}
                              </span>
                            ))}
                            {(item.tags || []).length > 3 && (
                              <span className="text-xs text-slate-400">+{item.tags.length - 3}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            item.status === 'active' ? 'bg-green-100 text-green-700' :
                            item.status === 'bounced' ? 'bg-red-100 text-red-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="p-2 text-xs text-slate-500">
                          {item.last_sent ? new Date(item.last_sent).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-2">
                          {item.status === 'active' && (
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0"
                                onClick={() => updateEmailStatus(item.email, 'bounced')}
                                title="Mark as Bounced"
                              >
                                <MailX className="h-3 w-3 text-red-500" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => updateEmailStatus(item.email, 'unsubscribed')}
                                title="Mark as Unsubscribed"
                              >
                                <UserX className="h-3 w-3 text-orange-500" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {allEmails.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No emails found. Click "Sync All Sources" to import emails from CRM.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Legacy Network View */}
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-semibold text-slate-900 mb-4">Email Lists by Network (Legacy)</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {Object.entries(emailStats.networks || {}).map(([network, count]) => (
                  <button
                    key={network}
                    onClick={() => setSelectedNetwork(network)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedNetwork === network 
                        ? 'bg-blue-600 text-white' 
                        : `${NETWORK_COLORS[network] || 'bg-gray-100 text-gray-700'} hover:opacity-80`
                    }`}
                  >
                    {network}: {count}
                  </button>
                ))}
              </div>

              {selectedNetwork && (
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="relative flex-1 max-w-xs">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search emails..."
                        value={emailSearch}
                        onChange={(e) => setEmailSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    <span className="text-sm text-slate-500">
                      {networkEmails.length} emails in {selectedNetwork}
                    </span>
                  </div>

                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {networkEmails.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 border rounded hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <Mail className="h-4 w-4 text-slate-400" />
                          <div>
                            <p className="text-sm font-medium">{item.email}</p>
                            {item.company && <p className="text-xs text-slate-500">{item.company}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            item.status === 'active' ? 'bg-green-100 text-green-700' :
                            item.status === 'bounced' ? 'bg-red-100 text-red-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {item.status}
                          </span>
                          {item.status === 'active' && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => updateEmailStatus(item.email, 'bounced')}>
                                <MailX className="h-4 w-4 text-red-500" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => updateEmailStatus(item.email, 'unsubscribed')}>
                                <UserX className="h-4 w-4 text-orange-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Offer Archive Email Repository Section */}
            <div className="bg-white rounded-lg border p-4 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Mail className="h-5 w-5 text-orange-500" />
                  Offer Inbox Repository
                  <span className="text-xs text-slate-500 font-normal">
                    (from offer@europacific.com)
                  </span>
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => syncOfferInbox(5000)}
                    disabled={offerSyncing}
                    className="border-orange-300 hover:bg-orange-50"
                  >
                    {offerSyncing ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Sync 5K Emails
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => syncOfferInbox(15000)}
                    disabled={offerSyncing}
                    className="border-orange-300 hover:bg-orange-50"
                  >
                    {offerSyncing ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Sync All (14K+)
                  </Button>
                </div>
              </div>

              {/* Stats */}
              {offerSyncStats && (
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-600">{offerSyncStats.total_emails}</p>
                    <p className="text-xs text-slate-500">Archived Emails</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{offerSyncStats.email_repository_count}</p>
                    <p className="text-xs text-slate-500">Unique Contacts</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{offerSyncStats.last_7_days}</p>
                    <p className="text-xs text-slate-500">Last 7 Days</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-sm font-medium text-slate-600">
                      {offerSyncStats.last_sync?.last_sync 
                        ? new Date(offerSyncStats.last_sync.last_sync).toLocaleString()
                        : "Never synced"
                      }
                    </p>
                    <p className="text-xs text-slate-500">Last Sync</p>
                  </div>
                </div>
              )}

              {/* Search and Export */}
              <div className="flex items-center gap-2 mb-4">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search emails..."
                    value={offerRepoSearch}
                    onChange={(e) => setOfferRepoSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={() => exportOfferRepository("csv")}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportOfferRepository("json")}>
                  <Download className="h-4 w-4 mr-2" />
                  Export JSON
                </Button>
                <span className="text-sm text-slate-500 ml-2">
                  {offerRepoTotal} contacts
                </span>
              </div>

              {/* Email List */}
              <div className="max-h-80 overflow-y-auto space-y-2">
                {offerRepoEmails.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No emails in repository yet. Click "Sync" to import from offer@europacific.com</p>
                  </div>
                ) : (
                  offerRepoEmails.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 border rounded hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-orange-400" />
                        <div>
                          <p className="text-sm font-medium">{item.email}</p>
                          {item.name && <p className="text-xs text-slate-500">{item.name}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">
                          {item.occurrence_count}x
                        </span>
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
                          offer
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Imported Email List Section */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Users className="h-5 w-5 text-purple-500" />
                    Imported Email List
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {importedEmailsTotal} subscribers from Import/Export
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.location.href = '/import-export'}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import More
                </Button>
              </div>

              {/* Tag filters */}
              {importedEmailTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => setSelectedImportedTag("")}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      !selectedImportedTag 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                    }`}
                  >
                    All ({importedEmailsTotal})
                  </button>
                  {importedEmailTags.slice(0, 15).map((t) => (
                    <button
                      key={t.tag}
                      onClick={() => setSelectedImportedTag(t.tag)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedImportedTag === t.tag 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {t.tag} ({t.count})
                    </button>
                  ))}
                </div>
              )}

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search imported emails..."
                  value={importedEmailSearch}
                  onChange={(e) => setImportedEmailSearch(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Email list */}
              <div className="max-h-80 overflow-y-auto space-y-2">
                {importedEmails.length === 0 ? (
                  <div className="text-center text-slate-500 py-8">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No imported emails yet</p>
                    <p className="text-sm mt-1">Go to Import/Export to upload your email list</p>
                  </div>
                ) : (
                  importedEmails.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 border rounded hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-purple-400" />
                        <div>
                          <p className="text-sm font-medium">{item.email}</p>
                          {item.name && <p className="text-xs text-slate-500">{item.name}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.tags?.map((tag, i) => (
                          <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* NEWSLETTERS TAB */}
          <TabsContent value="drafts" className="space-y-4">
            {/* Newsletter Stats Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg border p-4">
                <div className="text-sm text-slate-500">Emails Delivered</div>
                <div className="text-2xl font-bold text-green-600">{newsletterStats.newsletters.total_sent_count.toLocaleString()}</div>
                <div className="text-xs text-slate-400 mt-1">Successfully sent</div>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="text-sm text-slate-500">Failed Sends</div>
                <div className="text-2xl font-bold text-red-600">{newsletterStats.newsletters.total_failed_count.toLocaleString()}</div>
                <div className="text-xs text-slate-400 mt-1">Delivery failures</div>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="text-sm text-slate-500">Active Recipients</div>
                <div className="text-2xl font-bold text-blue-600">{newsletterStats.email_list.active.toLocaleString()}</div>
                <div className="text-xs text-slate-400 mt-1">In email list</div>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="text-sm text-slate-500">Bounced/Cleaned</div>
                <div className="text-2xl font-bold text-orange-600">
                  {(newsletterStats.email_list.bounced + newsletterStats.email_list.cleaned).toLocaleString()}
                </div>
                <div className="text-xs text-slate-400 mt-1">Removed from list</div>
              </div>
            </div>

            {/* Quick link to Unsubscribe Dashboard */}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4 flex items-center justify-between gap-3" data-testid="newsletter-unsub-banner">
              <div className="text-sm text-amber-900">
                <b>Unsubscribe Dashboard</b> — global do-not-mail list applies to every marketing send (newsletters + partner bulk + email archive). PF transactional emails still go through.
              </div>
              <a
                href="/unsubscribe-dashboard"
                className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold whitespace-nowrap"
                data-testid="newsletter-unsub-link"
              >
                Open dashboard →
              </a>
            </div>

            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Newsletter Drafts</h3>
              <Button onClick={() => openDraftEditor()} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                New Newsletter
              </Button>
            </div>

            <div className="space-y-4">
              {drafts.length === 0 ? (
                <div className="bg-white rounded-lg border p-12 text-center text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No newsletter drafts yet. Create your first newsletter!</p>
                </div>
              ) : (
                drafts.map((draft) => (
                  <div key={draft._id} className={`bg-white rounded-lg border p-4 ${draft.type === 'korea_lcl' ? 'border-l-4 border-l-blue-500' : ''}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <StatusBadge status={draft.status} />
                          {draft.type === 'korea_lcl' && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium flex items-center gap-1">
                              🇰🇷 Korea LCL
                            </span>
                          )}
                          {draft.scheduled_date && (
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                              <CalendarDays className="h-3 w-3" />
                              {new Date(draft.scheduled_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <h4 className="font-semibold text-slate-900">{draft.subject}</h4>
                        <p className="text-sm text-slate-500 mt-1">
                          Created {new Date(draft.created_at).toLocaleDateString()} {draft.created_by ? `by ${draft.created_by}` : ''}
                        </p>
                        {draft.target_lists?.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {draft.target_lists.map(list => (
                              <span key={list} className={`px-2 py-0.5 rounded text-xs ${NETWORK_COLORS[list] || 'bg-gray-100'}`}>
                                {list}
                              </span>
                            ))}
                          </div>
                        )}
                        {draft.target_tags?.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {draft.target_tags.map(tag => (
                              <span key={tag} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        {draft.schedule_items?.length > 0 && (
                          <p className="text-xs text-blue-600 mt-2">
                            {draft.schedule_items.length} vessels in schedule
                          </p>
                        )}
                        {draft.send_stats && (
                          <p className="text-xs text-green-600 mt-2">
                            Sent to {draft.send_stats.sent} recipients ({draft.send_stats.failed} failed)
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => previewNewsletter(draft._id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        {draft.status === 'draft' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => openDraftEditor(draft)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600" onClick={() => requestApproval(draft._id)}>
                              Request Approval
                            </Button>
                          </>
                        )}
                        
                        {draft.status === 'pending_approval' && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => approveDraft(draft._id)}>
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                        )}
                        
                        {draft.status === 'approved' && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => {
                                const testEmail = prompt("Enter test email address:");
                                if (testEmail) sendNewsletter(draft._id, testEmail);
                              }}
                            >
                              Send Test
                            </Button>
                            <Button 
                              size="sm" 
                              className="bg-purple-600 hover:bg-purple-700" 
                              onClick={() => openBatchSend(draft._id)}
                            >
                              <Users className="h-4 w-4 mr-1" />
                              Send in Batches
                            </Button>
                          </>
                        )}
                        
                        {draft.status === 'sending' && (
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2 text-blue-600">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span className="text-sm">
                                Sending... {draft.send_progress?.sent || 0}/{draft.send_progress?.total || '?'}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-800 hover:bg-red-50 border-red-300"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API_URL}/newsletter/drafts/${draft._id}/cancel-send`, {}, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success("Newsletter send cancelled");
                                  fetchDrafts();
                                } catch (err) {
                                  toast.error("Failed to cancel: " + (err.response?.data?.detail || err.message));
                                }
                              }}
                              data-testid={`cancel-send-${draft._id}`}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        )}
                        
                        {draft.status === 'sent' && (
                          <Button 
                            size="sm" 
                            className="bg-orange-600 hover:bg-orange-700" 
                            onClick={() => openBatchSend(draft._id)}
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Resend
                          </Button>
                        )}
                        
                        {draft.status !== 'sending' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200"
                            onClick={() => deleteDraft(draft._id)}
                            data-testid={`delete-draft-${draft._id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* ROAD CLOSURES TAB */}
          <TabsContent value="road-closures" className="space-y-4">
            <RoadClosureSection token={token} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Import Emails Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Email Addresses</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Network</Label>
              <Select value={importNetwork} onValueChange={setImportNetwork}>
                <SelectTrigger>
                  <SelectValue placeholder="Select network" />
                </SelectTrigger>
                <SelectContent>
                  {['WFN', 'TWIG', 'UOL', 'GAL', 'XLP', 'SBC', 'CRM'].map(n => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email Addresses (comma, semicolon, or newline separated)</Label>
              <Textarea
                value={importEmails}
                onChange={(e) => setImportEmails(e.target.value)}
                rows={10}
                placeholder="john@company.com, jane@company.com&#10;mike@company.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancel</Button>
            <Button onClick={handleImportEmails} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Draft Editor Dialog */}
      <Dialog open={showDraftEditor} onOpenChange={setShowDraftEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDraft ? 'Edit Newsletter' : 'Create Newsletter'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Subject Line</Label>
              <Input
                value={draftForm.subject}
                onChange={(e) => setDraftForm({...draftForm, subject: e.target.value})}
                placeholder="Newsletter subject..."
              />
            </div>
            
            <div>
              <Label>Greeting</Label>
              <Input
                value={draftForm.greeting}
                onChange={(e) => setDraftForm({...draftForm, greeting: e.target.value})}
              />
            </div>

            <div>
              <Label>Target Networks</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['WFN', 'TWIG', 'UOL', 'GAL', 'XLP', 'SBC', 'CRM'].map(network => (
                  <button
                    key={network}
                    type="button"
                    onClick={() => {
                      const lists = draftForm.target_lists.includes(network)
                        ? draftForm.target_lists.filter(n => n !== network)
                        : [...draftForm.target_lists, network];
                      setDraftForm({...draftForm, target_lists: lists});
                    }}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      draftForm.target_lists.includes(network)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {network}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Newsletter Sections</Label>
              <p className="text-xs text-slate-500 mb-2">Go to Articles tab to add articles to each section</p>
              <div className="space-y-2">
                {draftForm.sections.map((section, idx) => (
                  <div key={idx} className="border rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{section.emoji}</span>
                      <span className="font-medium">{section.title}</span>
                      <span className="text-sm text-slate-500">({section.articles.length} articles)</span>
                    </div>
                    {section.articles.length > 0 && (
                      <div className="space-y-1 pl-8">
                        {section.articles.map((articleId, aIdx) => {
                          const article = articles.find(a => a._id === articleId);
                          return (
                            <div key={aIdx} className="flex items-center justify-between text-sm text-slate-600">
                              <span>{article?.title || articleId}</span>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => removeArticleFromDraft(articleId, idx)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CTA Email</Label>
                <Input
                  value={draftForm.cta_email}
                  onChange={(e) => setDraftForm({...draftForm, cta_email: e.target.value})}
                />
              </div>
              <div>
                <Label>CTA Website</Label>
                <Input
                  value={draftForm.cta_website}
                  onChange={(e) => setDraftForm({...draftForm, cta_website: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Call to Action Text</Label>
              <Textarea
                value={draftForm.cta_text}
                onChange={(e) => setDraftForm({...draftForm, cta_text: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDraftEditor(false)}>Cancel</Button>
            <Button onClick={saveDraft} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Newsletter Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Newsletter Preview</DialogTitle>
          </DialogHeader>
          <div 
            className="border rounded-lg overflow-hidden"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Free Newsletter Preview Dialog */}
      <Dialog open={showFreeNewsletterPreview} onOpenChange={setShowFreeNewsletterPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Free Newsletter Preview
            </DialogTitle>
          </DialogHeader>
          <div 
            className="border rounded-lg overflow-hidden bg-white"
            dangerouslySetInnerHTML={{ __html: freeNewsletterPreview }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFreeNewsletterPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Newsletter Versions Modal */}
      <Dialog open={showAiVersionsModal} onOpenChange={setShowAiVersionsModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              AI Generated Newsletter Versions
              <span className="text-sm font-normal text-slate-500 ml-2">
                (Round {aiRefinementRound})
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {aiGenerating ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-purple-600 mb-4" />
              <p className="text-slate-600">Generating {aiRefinementRound === 1 ? '3 versions' : '3 refined versions'}...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {aiVersions.map((version, idx) => (
                <div
                  key={idx}
                  className={`border rounded-lg overflow-hidden transition-all hover:shadow-lg ${
                    selectedAiVersion?.style === version.style ? 'border-purple-500 ring-2 ring-purple-200' : 'border-slate-200'
                  }`}
                >
                  {/* Style Badge Header */}
                  <div className={`px-3 py-2 flex items-center justify-between ${
                    version.style === 'formal' || version.style === 'polished' ? 'bg-blue-50' :
                    version.style === 'friendly' || version.style === 'detailed' ? 'bg-green-50' :
                    'bg-orange-50'
                  }`}>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      version.style === 'formal' || version.style === 'polished' ? 'bg-blue-600 text-white' :
                      version.style === 'friendly' || version.style === 'detailed' ? 'bg-green-600 text-white' :
                      'bg-orange-600 text-white'
                    }`}>
                      {version.style?.toUpperCase()}
                    </span>
                    <button
                      onClick={() => setExpandedPreview(version)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Full Preview
                    </button>
                  </div>
                  
                  {/* Subject Line */}
                  <div className="px-3 py-2 bg-slate-50 border-b">
                    <p className="text-xs text-slate-500">Subject:</p>
                    <p className="text-sm font-medium truncate">{version.subject}</p>
                  </div>
                  
                  {/* HTML Preview (with images) */}
                  <div 
                    className="h-64 overflow-y-auto bg-white border-b"
                    style={{fontSize: '11px'}}
                    dangerouslySetInnerHTML={{ __html: version.html_preview || '<p>No preview available</p>' }}
                  />
                  
                  {/* Action Buttons */}
                  <div className="p-3 bg-white space-y-2">
                    <Button
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      size="sm"
                      onClick={() => selectAiVersion(version)}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Use This Version
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      size="sm"
                      onClick={() => refineAiVersion(version)}
                      disabled={aiGenerating}
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Refine This (3 More Options)
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {aiVersions.length === 0 && !aiGenerating && (
            <div className="text-center py-8 text-slate-500">
              <Sparkles className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No versions generated yet</p>
            </div>
          )}
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => {
                setAiRefinementRound(1);
                generateAiNewsletterVersions();
              }}
              disabled={aiGenerating}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Start Over
            </Button>
            <Button variant="outline" onClick={() => setShowAiVersionsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Preview Modal */}
      <Dialog open={!!expandedPreview} onOpenChange={() => setExpandedPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Full Newsletter Preview - {expandedPreview?.style?.toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          <div 
            className="border rounded-lg overflow-hidden"
            dangerouslySetInnerHTML={{ __html: expandedPreview?.html_preview || '' }}
          />
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setExpandedPreview(null)}>Close</Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={() => {
                selectAiVersion(expandedPreview);
                setExpandedPreview(null);
              }}
            >
              <Check className="h-4 w-4 mr-2" />
              Use This Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Korea LCL Preview Dialog */}
      <Dialog open={showKoreaLCLPreview} onOpenChange={setShowKoreaLCLPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🇰🇷 Korea LCL Newsletter Preview
            </DialogTitle>
          </DialogHeader>
          <div 
            className="border rounded-lg overflow-hidden bg-slate-100 p-2"
            dangerouslySetInnerHTML={{ __html: koreaLCLPreview }}
          />
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowKoreaLCLPreview(false)}>Close</Button>
            <Button onClick={createKoreaLCLDraft} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              <Send className="h-4 w-4 mr-2" /> Create Draft & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Send Dialog */}
      <Dialog open={showBatchSendDialog} onOpenChange={setShowBatchSendDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Send Newsletter in Batches
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <p className="font-medium mb-1">Why send in batches?</p>
              <p>Sending too many emails at once can get your domain blacklisted. Batch sending adds delays between emails and batches to maintain good deliverability.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Batch Size</Label>
                <Input
                  type="number"
                  value={batchSendConfig.batchSize}
                  onChange={(e) => setBatchSendConfig({ ...batchSendConfig, batchSize: parseInt(e.target.value) || 50 })}
                  min={10}
                  max={200}
                />
                <p className="text-xs text-slate-500 mt-1">Emails per batch (10-200)</p>
              </div>
              <div>
                <Label>Delay Between Emails (sec)</Label>
                <Input
                  type="number"
                  value={batchSendConfig.delayBetweenEmails}
                  onChange={(e) => setBatchSendConfig({ ...batchSendConfig, delayBetweenEmails: parseFloat(e.target.value) || 0.5 })}
                  min={0.1}
                  max={10}
                  step={0.1}
                />
                <p className="text-xs text-slate-500 mt-1">Seconds between each email (0.5 = ~25 min for 4k emails)</p>
              </div>
              <div>
                <Label>Delay Between Batches (sec)</Label>
                <Input
                  type="number"
                  value={batchSendConfig.delayBetweenBatches}
                  onChange={(e) => setBatchSendConfig({ ...batchSendConfig, delayBetweenBatches: parseInt(e.target.value) || 10 })}
                  min={5}
                  max={300}
                />
                <p className="text-xs text-slate-500 mt-1">Pause between batches (5-300)</p>
              </div>
              <div>
                <Label>Skip Recent (days)</Label>
                <Input
                  type="number"
                  value={batchSendConfig.excludeRecentDays}
                  onChange={(e) => setBatchSendConfig({ ...batchSendConfig, excludeRecentDays: parseInt(e.target.value) || 7 })}
                  min={0}
                  max={30}
                />
                <p className="text-xs text-slate-500 mt-1">Don't send to recent recipients</p>
              </div>
            </div>

            <div>
              <Label>Filter by Tags (optional)</Label>
              <div className="flex flex-wrap gap-2 mt-2 p-2 border rounded bg-slate-50 max-h-32 overflow-y-auto">
                {allEmailsTags.length > 0 ? allEmailsTags.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      const tags = batchSendConfig.targetTags.includes(tag)
                        ? batchSendConfig.targetTags.filter(t => t !== tag)
                        : [...batchSendConfig.targetTags, tag];
                      setBatchSendConfig({ ...batchSendConfig, targetTags: tags });
                    }}
                    className={`px-2 py-1 rounded text-xs font-medium transition-all flex items-center gap-1 ${
                      batchSendConfig.targetTags.includes(tag)
                        ? 'bg-purple-600 text-white ring-2 ring-purple-300 shadow-sm'
                        : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-100 hover:border-slate-400'
                    }`}
                  >
                    {batchSendConfig.targetTags.includes(tag) && (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {tag}
                  </button>
                )) : <span className="text-xs text-slate-400">No tags available - sync email lists first</span>}
              </div>
              <p className="text-xs text-slate-500 mt-1">Leave empty to send to all active emails</p>
            </div>

            <div>
              <Label>Filter by Networks (optional)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {allEmailsNetworks.map(network => (
                  <button
                    key={network}
                    type="button"
                    onClick={() => {
                      const networks = batchSendConfig.targetNetworks.includes(network)
                        ? batchSendConfig.targetNetworks.filter(n => n !== network)
                        : [...batchSendConfig.targetNetworks, network];
                      setBatchSendConfig({ ...batchSendConfig, targetNetworks: networks });
                    }}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-all flex items-center gap-1.5 ${
                      batchSendConfig.targetNetworks.includes(network)
                        ? 'bg-purple-600 text-white ring-2 ring-purple-300 shadow-md'
                        : `${NETWORK_COLORS[network] || 'bg-gray-100 text-gray-700'} border-2 border-transparent hover:border-slate-300`
                    }`}
                  >
                    {batchSendConfig.targetNetworks.includes(network) && (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {network}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {batchSendConfig.targetNetworks.length > 0 
                  ? `Selected: ${batchSendConfig.targetNetworks.join(', ')}` 
                  : 'Click to select networks (none selected = all networks)'}
              </p>
            </div>

            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm font-medium text-slate-700">Estimated Active Recipients:</p>
              <p className="text-2xl font-bold text-purple-600">{allEmailsStats.active}</p>
              <p className="text-xs text-slate-500">
                {batchSendConfig.targetTags.length > 0 || batchSendConfig.targetNetworks.length > 0 
                  ? "Actual count may be lower based on filters" 
                  : "All active emails will receive this newsletter"}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchSendDialog(false)}>Cancel</Button>
            <Button 
              onClick={sendNewsletterBatch} 
              disabled={sendingBatch || allEmailsStats.active === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {sendingBatch ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Start Batch Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Progress Toast/Banner */}
      {sendProgress && sendProgress.status === 'sending' && (
        <div className="fixed bottom-4 right-4 bg-white border shadow-lg rounded-lg p-4 z-50 max-w-sm">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            <div>
              <p className="font-medium">Sending Newsletter...</p>
              <p className="text-sm text-slate-600">
                {sendProgress.send_progress?.sent || 0} / {sendProgress.send_progress?.total || '?'} emails sent
              </p>
              <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all" 
                  style={{ width: `${((sendProgress.send_progress?.sent || 0) / (sendProgress.send_progress?.total || 1)) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Newsletter Detail Modal */}
      <Dialog open={showArchiveDetailModal} onOpenChange={setShowArchiveDetailModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              {selectedArchiveNewsletter?.subject || "Newsletter Detail"}
            </DialogTitle>
          </DialogHeader>
          {selectedArchiveNewsletter && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                <span>Sent: {new Date(selectedArchiveNewsletter.sent_at).toLocaleString()}</span>
                <span className="text-green-600">{selectedArchiveNewsletter.sent_count} delivered</span>
                {selectedArchiveNewsletter.failed_count > 0 && (
                  <span className="text-red-500">{selectedArchiveNewsletter.failed_count} failed</span>
                )}
                {selectedArchiveNewsletter.suppressed_count > 0 && (
                  <span className="text-amber-500">{selectedArchiveNewsletter.suppressed_count} suppressed</span>
                )}
              </div>
              
              {/* HTML Preview */}
              {selectedArchiveNewsletter.html_preview ? (
                <div 
                  className="border rounded-lg bg-white p-4"
                  dangerouslySetInnerHTML={{ __html: selectedArchiveNewsletter.html_preview }}
                />
              ) : (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <pre className="whitespace-pre-wrap text-sm">{selectedArchiveNewsletter.content}</pre>
                </div>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => copyNewsletterToEditor(selectedArchiveNewsletter)}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy to Editor
                </Button>
                <Button variant="outline" onClick={() => setShowArchiveDetailModal(false)}>
                  Close
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add to Suppression List Modal */}
      <Dialog open={showAddSuppressionModal} onOpenChange={setShowAddSuppressionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Email to Suppression List</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={newSuppressionEmail}
                onChange={(e) => setNewSuppressionEmail(e.target.value)}
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Select value={newSuppressionReason} onValueChange={setNewSuppressionReason}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual Block</SelectItem>
                  <SelectItem value="hard_bounce">Hard Bounce</SelectItem>
                  <SelectItem value="soft_bounce">Soft Bounce</SelectItem>
                  <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                  <SelectItem value="complaint">Complaint</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSuppressionModal(false)}>Cancel</Button>
            <Button onClick={addToSuppressionList}>
              <Ban className="h-4 w-4 mr-2" />
              Add to Suppression
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
